import { useMemo, useState } from 'react';
import { addDays, format, isSameDay } from 'date-fns';
import clsx from 'clsx';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useSimulationStore } from '@/state/simulationStore';
import { startOptionsForDay, slotStartIso, SELF_BOOK_STARTS } from '@/domain/selfBookFlow';
import { resolveDayOff } from '@/domain/artistAdmin';
import type { Artist } from '@/domain/types';

interface Props {
  artist: Artist;
  onHold: (startIso: string) => void;
  heldStartIso: string | null;
  readOnly?: boolean;
}

const START_LABEL: Record<(typeof SELF_BOOK_STARTS)[number], string> = {
  '11:00': '11:00am',
  '15:00': '3:00pm',
};

export default function ArtistSelfBookWeek({ artist, onHold, heldStartIso, readOnly }: Props) {
  const clockIso = useSimulationStore((s) => s.clockIso);
  // Week navigation (K4): 0 = this week; can page forward, back only to this week
  // (self-book never holds a past slot).
  const [weekOffset, setWeekOffset] = useState(0);

  const days = useMemo(() => {
    const start = addDays(new Date(clockIso), weekOffset * 7);
    return Array.from({ length: 7 }, (_, i) => addDays(start, i)).map((day) => {
      const off = !!resolveDayOff(artist.availability, day);
      const openStarts = new Set(startOptionsForDay(artist, day, 'small'));
      return { day, off, openStarts };
    });
  }, [artist, clockIso, weekOffset]);

  const rangeLabel = `${format(days[0]!.day, 'EEE d MMM')} — ${format(days[6]!.day, 'd MMM')}`;

  return (
    <div className="flex flex-col gap-2" data-testid="artist-self-book-week">
      <div className="flex items-center justify-between px-1" data-testid="selfbook-week-nav">
        <button
          type="button"
          data-testid="selfbook-week-prev"
          disabled={weekOffset === 0}
          onClick={() => setWeekOffset((o) => Math.max(0, o - 1))}
          className="inline-flex items-center gap-1 rounded-md border border-gold-dim/30 px-2 py-1 font-inter text-[11px] text-cream-soft disabled:opacity-40 hover:border-gold-bright/50"
        >
          <ChevronLeft size={13} /> Prev
        </button>
        <span className="font-inter text-xs text-cream inline-flex items-center gap-2">
          {rangeLabel}
          {weekOffset > 0 ? (
            <button
              type="button"
              data-testid="selfbook-week-today"
              onClick={() => setWeekOffset(0)}
              className="font-inter text-[10px] uppercase tracking-widest text-gold-bright"
            >
              This week
            </button>
          ) : null}
        </span>
        <button
          type="button"
          data-testid="selfbook-week-next"
          onClick={() => setWeekOffset((o) => o + 1)}
          className="inline-flex items-center gap-1 rounded-md border border-gold-dim/30 px-2 py-1 font-inter text-[11px] text-cream-soft hover:border-gold-bright/50"
        >
          Next <ChevronRight size={13} />
        </button>
      </div>
      {days.map(({ day, off, openStarts }) => (
        <div
          key={day.toISOString()}
          className={clsx('rounded-lg border border-gold-dim/30 bg-ink-black px-3 py-2', off && 'opacity-60')}
        >
          <div className="flex items-baseline justify-between">
            <span className="font-inter text-xs font-bold tracking-wide text-cream">
              <span className="text-cream-faint mr-1.5">{format(day, 'EEE').toUpperCase()}</span>
              {format(day, 'd MMM')}
            </span>
            {off ? (
              <span className="font-inter text-[9px] uppercase tracking-widest text-cream-faint">Rostered off</span>
            ) : null}
          </div>
          {off ? null : (
            <div className="mt-2 grid grid-cols-2 gap-2">
              {SELF_BOOK_STARTS.map((start) => {
                const iso = slotStartIso(day, start);
                const isHeld = heldStartIso
                  ? isSameDay(new Date(heldStartIso), day) && new Date(heldStartIso).getHours() === parseInt(start, 10)
                  : false;
                const open = openStarts.has(start) || isHeld;
                if (isHeld) {
                  return (
                    <span
                      key={start}
                      data-testid={`selfbook-held-slot-${start}`}
                      className="rounded-md border border-gold-bright bg-gold-bright px-2 py-1.5 text-center font-mono text-xs font-semibold text-ink-black"
                    >
                      {START_LABEL[start]} · holding
                    </span>
                  );
                }
                if (!open) {
                  return (
                    <span
                      key={start}
                      className="rounded-md border border-dashed border-cream-faint/40 px-2 py-1.5 text-center font-mono text-xs text-cream-faint"
                    >
                      {START_LABEL[start]} · booked
                    </span>
                  );
                }
                return readOnly ? (
                  <span
                    key={start}
                    className="rounded-md border border-gold-bright/60 px-2 py-1.5 text-center font-mono text-xs text-gold-bright"
                  >
                    {START_LABEL[start]} · open
                  </span>
                ) : (
                  <button
                    key={start}
                    type="button"
                    data-testid={`selfbook-open-slot-${start}`}
                    data-start-iso={iso}
                    onClick={() => onHold(iso)}
                    className="rounded-md border border-gold-bright bg-gold/10 px-2 py-1.5 text-center font-mono text-xs font-semibold text-gold-bright hover:bg-gold/20"
                  >
                    {START_LABEL[start]} · hold
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
